import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Users, Clock, CalendarDays, Sparkles, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Eyebrow, SectionHeading } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { SelfStudyDialog, MembershipDialog } from "@/components/StudioFlows";
import { useDialogs } from "@/components/GlobalDialogs";
import { getStudio, getSettings, getEvents } from "@/lib/api";

const GALLERY = [
  "https://images.unsplash.com/photo-1758627506826-0658170e5cf6?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1763630730215-d97346f23065?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
  "https://images.unsplash.com/photo-1558470598-a5dda9640f68?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
];

export default function Studio() {
  const { openCollaborate } = useDialogs();
  const [data, setData] = useState({ facilities: [] });
  const [settings, setSettings] = useState(null);
  const [liveStudy, setLiveStudy] = useState([]);
  const [ssOpen, setSsOpen] = useState(false);
  const [memOpen, setMemOpen] = useState(false);

  useEffect(() => {
    getStudio().then(setData).catch(() => {});
    getSettings().then(setSettings).catch(() => {});
    getEvents("live-study").then(setLiveStudy).catch(() => {});
  }, []);

  const ss = settings?.self_study || { effective: 49, regular: 99, launch_active: true };
  const mem = settings?.membership || { effective: 499, regular: 999, benefits: [] };

  return (
    <div>
      <section className="relative overflow-hidden border-b border-border noise">
        <div className="ytca-container py-16 sm:py-20">
          <Eyebrow>YTCA Studio</Eyebrow>
          <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold tracking-tight sm:text-6xl">A study space for artists to practise and learn.</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">Book the studio for focused self-study and practice, join as a member, or collaborate with us to run a study session. Real dates, real availability, instant booking.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button onClick={() => setSsOpen(true)} size="lg" className="rounded-full" data-testid="hero-self-study-button">Book Self-Study</Button>
            <Button onClick={openCollaborate} variant="outline" size="lg" className="rounded-full" data-testid="hero-collaborate-button">Collaborate with us</Button>
          </div>
        </div>
      </section>

      <div className="ytca-container py-12">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {GALLERY.map((g, i) => (
            <Reveal key={i} delay={i * 0.06} className={i === 0 ? "col-span-2 sm:col-span-1" : ""}>
              <div className="overflow-hidden rounded-2xl border border-border"><img src={g} alt="YTCA studio" className="aspect-[4/3] w-full object-cover" /></div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* About + facilities */}
      <section className="ytca-section">
        <div className="ytca-container grid gap-10 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow="About the Studio" title="Warm, flexible and made for making." />
            <p className="mt-4 text-lg leading-relaxed text-foreground/80">Natural light, an open floor and a welcoming atmosphere — the studio adapts to whatever you need, whether that's a quiet afternoon of practice or a focused study session with fellow artists.</p>
          </div>
          <Card className="rounded-2xl border-border bg-secondary/40 p-6">
            <h3 className="font-display text-lg font-semibold">Facilities</h3>
            <ul className="mt-4 space-y-3">
              {(data.facilities || []).map((f, i) => (
                <li key={i} className="flex items-start gap-3"><Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span className="text-foreground/80">{f}</span></li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      {/* Conversion offerings */}
      <section id="book" className="ytca-section bg-secondary/40">
        <div className="ytca-container">
          <SectionHeading eyebrow="Use the Studio" title="Study space for artists — and open to collaboration." intro="Book focused self-study time, join as a member, or reach out by email to collaborate with us." />

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            {/* Self-Study */}
            <Reveal>
              <Card className="flex h-full flex-col rounded-2xl border-border bg-card p-6">
                <div className="flex items-center justify-between">
                  <Users className="h-7 w-7 text-primary" />
                  {ss.launch_active && <Badge className="rounded-full bg-accent text-accent-foreground">Special Launch Price</Badge>}
                </div>
                <h3 className="mt-4 font-display text-xl font-semibold">Self-Study Groups</h3>
                <p className="mt-1 flex-1 text-sm text-muted-foreground">For students and emerging artists using the studio for independent practice and study. Price is per person, per hour.</p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-3xl font-semibold text-primary">₹{ss.effective}</span>
                  {ss.launch_active && <span className="text-muted-foreground line-through">₹{ss.regular}</span>}
                  <span className="text-sm text-muted-foreground">/person/hour</span>
                </div>
                <Button onClick={() => setSsOpen(true)} className="mt-5 rounded-full" data-testid="offering-self-study-button">Book Self-Study <ArrowRight className="h-4 w-4" /></Button>
              </Card>
            </Reveal>

            {/* Collaborate with us (via email) */}
            <Reveal delay={0.05}>
              <Card className="flex h-full flex-col rounded-2xl border-border bg-card p-6">
                <div className="flex items-center justify-between"><Users className="h-7 w-7 text-primary" /></div>
                <h3 className="mt-4 font-display text-xl font-semibold">Collaborate with us</h3>
                <p className="mt-1 flex-1 text-sm text-muted-foreground">Educators, artists and institutions who'd like to work with us on classes, study sessions or community art projects can reach out by email. We read every message.</p>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-xl font-semibold text-primary">Get in touch</span>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Drop us an email and we'll take it from there.</p>
                <Button onClick={openCollaborate} className="mt-4 rounded-full" data-testid="offering-collaborate-button">Collaborate with us <ArrowRight className="h-4 w-4" /></Button>
              </Card>
            </Reveal>
          </div>

          {/* Membership */}
          <Reveal delay={0.1}>
            <Card className="mt-6 overflow-hidden rounded-3xl border-border bg-[#141414] text-white noise">
              <div className="grid gap-6 p-8 md:grid-cols-[1.3fr_0.7fr] md:items-center">
                <div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-6 w-6 text-primary" />
                    <Badge className="rounded-full bg-accent text-accent-foreground">Special Launch Price</Badge>
                  </div>
                  <h3 className="mt-3 font-display text-2xl font-semibold">YTCA Membership Pass</h3>
                  <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                    {(mem.benefits || []).map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-white/80"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{b}</li>
                    ))}
                  </ul>
                </div>
                <div className="text-center md:text-right">
                  <div className="flex items-baseline justify-center gap-2 md:justify-end">
                    <span className="font-display text-4xl font-semibold">₹{mem.effective}</span>
                    <span className="text-white/60">/ month</span>
                  </div>
                  <p className="text-sm text-white/50 line-through">₹{mem.regular}/month</p>
                  <Button onClick={() => setMemOpen(true)} size="lg" className="mt-4 rounded-full" data-testid="offering-membership-button">Join Membership</Button>
                  <p className="mt-2 text-xs text-white/50">Recurring monthly · cancel anytime</p>
                </div>
              </div>
            </Card>
          </Reveal>

          {/* Live Study Sessions */}
          <div className="mt-14">
            <SectionHeading eyebrow="Live Study Sessions" title="Draw from a live model — ₹249 per person" intro="Each session is individually bookable. Pick a date that works for you." />
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {liveStudy.map((s, i) => (
                <Reveal key={s.slug} delay={i * 0.05}>
                  <Card className="card-lift flex h-full flex-col overflow-hidden rounded-2xl border-border bg-card">
                    <img src={s.image} alt={s.title} className="aspect-[16/10] w-full object-cover" />
                    <div className="flex flex-1 flex-col p-5">
                      <h3 className="font-display text-lg font-semibold">{s.title}</h3>
                      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><CalendarDays className="h-3.5 w-3.5" />{s.date}</p>
                      {typeof s.spots_left === "number" && <p className="mt-1 text-xs text-muted-foreground">{s.spots_left} of {s.capacity} spots left</p>}
                      <div className="mt-auto flex items-center justify-between pt-4">
                        <span className="font-display text-lg font-semibold">₹{s.price}</span>
                        <Button asChild size="sm" className="rounded-full" data-testid={`live-study-book-${s.slug}`}><Link to={`/events/${s.slug}`}>Book Session</Link></Button>
                      </div>
                    </div>
                  </Card>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SelfStudyDialog open={ssOpen} setOpen={setSsOpen} settings={settings} />
      <MembershipDialog open={memOpen} setOpen={setMemOpen} settings={settings} />
    </div>
  );
}
